/**
 * api/admin.js — clients, RBAC, approved bookers, driver shadow create,
 * public booking links + submit RPC, doc requirements view.
 */
import { supabase } from "@/lib/supabaseClient";
import { backendCall, BACKEND } from "./_shared";
import { nullEmpty } from "@/lib/constants";

/* Clients --------------------------------------------------------------- */

export async function listClients() {
  return supabase
    .from("lbs_clients")
    .select("id,name,slug,is_active,brand_logo_url,brand_logo_path,brand_primary_color,auto_archive_after_days")
    .eq("is_active", true)
    .order("name", { ascending: true });
}

/**
 * Separate fetcher for the buffer-feature master switch. Lives outside
 * listClients() so that pages which don't need it (90% of callers) aren't
 * broken when the column doesn't exist yet (pre-migration 150). Returns
 * { enabled: boolean, supported: boolean } — `supported` tells the UI whether
 * to render the toggle at all.
 */
export async function getClientBufferFlag(clientId) {
  if (!clientId) return { enabled: false, supported: false };
  const { data, error } = await supabase
    .from("lbs_clients")
    .select("buffer_time_enabled")
    .eq("id", clientId)
    .maybeSingle();
  if (error) {
    // 42703 = undefined_column (migration 150 not yet applied).
    if (error.code === "42703" || /buffer_time_enabled/i.test(error.message || "")) {
      return { enabled: false, supported: false };
    }
    return { enabled: false, supported: true };
  }
  return { enabled: !!data?.buffer_time_enabled, supported: true };
}

/** Tenant-wide bay auto-assign flag (migration 157). supported=false pre-migration. */
export async function getClientAutoAssignFlag(clientId) {
  if (!clientId) return { enabled: false, supported: false };
  const { data, error } = await supabase.from("lbs_clients").select("bay_auto_assign").eq("id", clientId).maybeSingle();
  if (error) return { enabled: false, supported: !(error.code === "42703" || /bay_auto_assign/i.test(error.message || "")) };
  return { enabled: !!data?.bay_auto_assign, supported: true };
}

export async function updateClientSettings(id, values) {
  return supabase.from("lbs_clients").update(values).eq("id", id).select().maybeSingle();
}

/**
 * Upload a tenant logo into the per-tenant Supabase Storage bucket `lbs-<slug>`.
 * Mirrors the uploadSiteRuleFile pattern so the same arrayBuffer workaround applies.
 */
export async function uploadClientLogo(client, file) {
  if (!client?.slug) return { error: { message: "Client slug missing" } };
  const ext = (file.name?.split(".").pop() || "png").toLowerCase();
  const path = `branding/logo-${Date.now()}.${ext}`;
  const bucket = `lbs-${client.slug}`;

  let buffer;
  try {
    buffer = await file.arrayBuffer();
  } catch (e) {
    return { error: { message: e?.message || "Could not read file" } };
  }
  try {
    const up = await supabase.storage.from(bucket).upload(path, buffer, {
      contentType: file.type || "application/octet-stream",
      upsert: true,
    });
    if (up.error) return { error: { message: up.error.message || "Upload failed" } };
  } catch (e) {
    return { error: { message: e?.message || "Upload failed" } };
  }
  const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
  return { data: { url: pub.publicUrl, path, bucket } };
}

/* Reservation types ----------------------------------------------------- */

export async function listReservationTypes({ clientId = null, includeInactive = false } = {}) {
  let q = supabase
    .from("lbs_reservation_types")
    .select("*")
    .order("sort_order", { ascending: true })
    .order("name", { ascending: true });
  if (clientId) q = q.eq("client_id", clientId);
  if (!includeInactive) q = q.eq("is_active", true);
  return q;
}

export async function createReservationType(values) {
  return supabase.from("lbs_reservation_types").insert(values).select().maybeSingle();
}

export async function updateReservationType(id, values) {
  const payload = { ...values };
  delete payload.id;
  delete payload.client_id;
  delete payload.created_at;
  delete payload.updated_at;
  return supabase
    .from("lbs_reservation_types")
    .update(payload)
    .eq("id", id)
    .select()
    .maybeSingle();
}

export async function deleteReservationType(id) {
  return supabase.from("lbs_reservation_types").delete().eq("id", id).select();
}

/* Picklist field layout (Phase 1A — admin UI persistence only;
   booking-form layout-driven rendering arrives in Phase 1B) ------------- */

export async function listPicklistFieldLayout(clientId) {
  // Return tenant-specific rows if present, otherwise the global defaults.
  // We fetch BOTH so the UI can show "inherited from global".
  return supabase
    .from("lbs_picklist_field_layout")
    .select("*")
    .or(`client_id.eq.${clientId},client_id.is.null`)
    .order("client_id", { ascending: false, nullsFirst: false })
    .order("target_tab")
    .order("target_position");
}

export async function upsertPicklistFieldLayout(row) {
  // row: { client_id, category, target_tab, target_position }
  return supabase
    .from("lbs_picklist_field_layout")
    .upsert(row, { onConflict: "client_id,category" })
    .select()
    .maybeSingle();
}

/* Users / RBAC ---------------------------------------------------------- */

export async function listUserDirectory() {
  return supabase
    .from("lbs_v_user_directory")
    .select("*")
    .order("created_at", { ascending: false });
}

export const inviteUser     = (payload) => backendCall("/api/users/invite", payload);
export const inviteStatus   = (userIds) => backendCall("/api/users/invite-status", { user_ids: userIds });
export const resendInvite   = (userId, clientId = null) => backendCall("/api/users/resend-invite", { user_id: userId, client_id: clientId });
export const deactivateUser = (userId)  => backendCall("/api/users/deactivate", { user_id: userId });
export const setTempPassword = (userId) => backendCall("/api/users/temp-password", { user_id: userId });

/**
 * Soft-delete a user: stamps deleted_at, sets is_active=false, removes
 * all tenant assignments. Bookings stay intact. Auth row untouched.
 */
export async function softDeleteUser(userId) {
  return supabase.rpc("lbs_soft_delete_user", { p_user_id: userId });
}

export async function addUserAssignment(values) {
  return supabase
    .from("lbs_user_assignments")
    .insert(values)
    .select()
    .maybeSingle();
}

export async function updateUserAssignment(id, values) {
  return supabase
    .from("lbs_user_assignments")
    .update(values)
    .eq("id", id)
    .select()
    .maybeSingle();
}

export async function deleteUserAssignment(id) {
  return supabase.from("lbs_user_assignments").delete().eq("id", id).select();
}

/** Available FLDs (forklift drivers) — any active user in the directory. */
export async function listForkliftDrivers() {
  return supabase
    .from("lbs_v_user_directory")
    .select("user_id, email, first_name, last_name, assignments")
    .order("first_name", { ascending: true })
    .limit(500);
}

/* Public booking links -------------------------------------------------- */

export async function listPublicLinks() {
  return supabase
    .from("lbs_public_booking_links")
    .select(
      `id, slug, title, description, is_active, requires_approval,
       allowed_directions, max_bookings_per_day, advance_booking_days,
       client_id, site_id, created_at,
       site:lbs_sites!lbs_public_booking_links_site_id_fkey(name,code)`
    )
    .order("created_at", { ascending: false });
}

export async function upsertPublicLink(values) {
  if (values.id) {
    return supabase
      .from("lbs_public_booking_links")
      .update(values)
      .eq("id", values.id)
      .select()
      .maybeSingle();
  }
  return supabase
    .from("lbs_public_booking_links")
    .insert(values)
    .select()
    .maybeSingle();
}

export async function deletePublicLink(id) {
  return supabase.from("lbs_public_booking_links").delete().eq("id", id).select();
}

export async function getPublicLinkBySlug(slug) {
  return supabase
    .from("lbs_public_booking_links")
    .select(`
      id, slug, title, description, allowed_directions, advance_booking_days,
      is_active, client_id, site_id,
      client:lbs_clients(slug, name),
      site:lbs_sites(name, code, timezone)
    `)
    .eq("slug", slug)
    .eq("is_active", true)
    .maybeSingle();
}

export async function submitPublicBooking(slug, payload) {
  return supabase.rpc("lbs_submit_public_booking", {
    p_slug: slug,
    p_payload: payload,
  });
}

/**
 * Fetch the tenant's Terms of Service for a public booking link slug.
 * Anon-callable (SECURITY DEFINER); blocks if the link is inactive.
 * Returns { client_id, client_name, tos_markdown, tos_version }.
 */
export async function getPublicLinkTos(slug) {
  return supabase.rpc("lbs_get_public_tos", { p_slug: slug });
}

/** Anonymous tenant branding (logo/name) for public pages. Resolves null on miss. */
export async function getPublicBranding(query) {
  const params = new URLSearchParams(
    Object.entries(query || {}).filter(([, v]) => v),
  );
  if (![...params].length) return null;
  try {
    const r = await fetch(`${BACKEND}/api/public/branding?${params}`);
    return r.ok ? await r.json() : null;
  } catch (_e) {
    return null;
  }
}

/** Anonymous availability inputs for a public link's bay on one date. */
export async function getPublicAvailability(linkSlug, date) {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const r = await fetch(`${BACKEND}/api/public/availability?${new URLSearchParams({ link_slug: linkSlug, date })}`);
      if (r.ok) return await r.json();
    } catch (_e) { /* retry once */ }
  }
  return null;
}

/* Approved bookers ------------------------------------------------------ */

export async function listApprovedBookers({ clientId = null, includeInactive = false } = {}) {
  let q = supabase
    .from("lbs_approved_bookers")
    .select("id,client_id,name,abn,contact_email,contact_phone,is_active,auto_approve_bookings,notes,created_at,updated_at")
    .order("name", { ascending: true });
  if (clientId) q = q.eq("client_id", clientId);
  if (!includeInactive) q = q.eq("is_active", true);
  return q;
}

export async function getApprovedBooker(id) {
  return supabase
    .from("lbs_approved_bookers")
    .select("*")
    .eq("id", id)
    .maybeSingle();
}

export async function createApprovedBooker(values) {
  return supabase
    .from("lbs_approved_bookers")
    .insert(nullEmpty(values))
    .select()
    .maybeSingle();
}

export async function updateApprovedBooker(id, values) {
  const payload = nullEmpty(values);
  delete payload.id;
  delete payload.client_id;
  delete payload.created_at;
  return supabase
    .from("lbs_approved_bookers")
    .update(payload)
    .eq("id", id)
    .select()
    .maybeSingle();
}

export async function deactivateApprovedBooker(id) {
  return supabase
    .from("lbs_approved_bookers")
    .update({ is_active: false, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .maybeSingle();
}

export async function inviteApprovedBookerLogin({ bookerId, contactEmail, redirectTo = null }) {
  const { data, error } = await backendCall("/api/admin/approved-bookers/invite",
    { booker_id: bookerId, contact_email: contactEmail, redirect_to: redirectTo });
  if (error) throw new Error(error.message || "Invite failed");
  return data;
}

/* Drivers (shadow create + search) ------------------------------------- */

export async function createDriver({ firstName, lastName, email, phone = null, licenceNumber = null, licenceType = null, licenceState = null, clientId }) {
  const { data, error } = await backendCall("/api/admin/drivers/create", {
    first_name:      firstName,
    last_name:       lastName,
    email,
    phone,
    licence_number:  licenceNumber,
    licence_type:    licenceType,
    licence_state:   licenceState,
    client_id:       clientId,
  });
  if (error) throw new Error(error.message || "Driver create failed");
  return data;
}

/** Carrier users: only their own company's drivers (migration 163 RPC). */
export async function searchCompanyDrivers({ q = "", ids = null, limit = 20 } = {}) {
  const { data, error } = await supabase.rpc("lbs_search_company_drivers", {
    p_q: (q || "").trim(), p_ids: ids, p_limit: limit,
  });
  return { data: (data || []).map((d) => ({ ...d, _scope: "booker" })), error };
}

export async function searchDrivers({ q = "", clientId = null, bookerId = null, limit = 20, companyOnly = false, ids = null } = {}) {
  if (companyOnly) return searchCompanyDrivers({ q, ids, limit });
  const term = (q || "").trim();
  // Staff: drivers of THIS tenant only (migration 164 RPC).
  let qb = clientId
    ? supabase.rpc("lbs_search_tenant_drivers", { p_client: clientId, p_q: term, p_ids: ids, p_limit: limit })
    : supabase
        .from("lbs_user_profiles")
        .select("id,email,first_name,last_name,phone,phone_normalised")
        .limit(limit);

  if (term && !clientId) {
    const digits = term.replace(/\D/g, "");
    const ors = [
      `email.ilike.%${term}%`,
      `first_name.ilike.%${term}%`,
      `last_name.ilike.%${term}%`,
    ];
    if (digits) ors.push(`phone_normalised.ilike.%${digits}%`);
    qb = qb.or(ors.join(","));
  }
  const { data, error } = await qb;
  if (error) return { data: [], error };

  let bookerSet = new Set(), tenantSet = new Set();
  if (bookerId && data?.length) {
    const ids = data.map((d) => d.id);
    const { data: rows } = await supabase
      .from("lbs_bookings")
      .select("assigned_driver_id")
      .in("assigned_driver_id", ids)
      .eq("approved_booker_id", bookerId);
    bookerSet = new Set((rows || []).map((r) => r.assigned_driver_id));
  }
  if (clientId && data?.length) {
    const ids = data.map((d) => d.id);
    const { data: rows } = await supabase
      .from("lbs_bookings")
      .select("assigned_driver_id")
      .in("assigned_driver_id", ids)
      .eq("client_id", clientId);
    tenantSet = new Set((rows || []).map((r) => r.assigned_driver_id));
  }

  const score = (d) =>
    (bookerSet.has(d.id) ? 0 : tenantSet.has(d.id) ? 1 : 2);
  const sorted = [...(data || [])].sort((a, b) => {
    const sa = score(a), sb = score(b);
    if (sa !== sb) return sa - sb;
    const an = `${a.last_name || ""} ${a.first_name || ""}`.trim();
    const bn = `${b.last_name || ""} ${b.first_name || ""}`.trim();
    return an.localeCompare(bn);
  });

  return {
    data: sorted.map((d) => ({
      ...d,
      _scope: bookerSet.has(d.id) ? "booker" : tenantSet.has(d.id) ? "tenant" : "global",
    })),
    error: null,
  };
}

export async function listDriversByApprovedBooker(bookerId) {
  const { data, error } = await supabase
    .from("lbs_bookings")
    .select("assigned_driver_id, scheduled_start")
    .eq("approved_booker_id", bookerId)
    .not("assigned_driver_id", "is", null)
    .order("scheduled_start", { ascending: false });
  if (error) return { data: [], error };
  const seen = new Set();
  const ids = [];
  for (const r of data || []) {
    if (!seen.has(r.assigned_driver_id)) {
      seen.add(r.assigned_driver_id);
      ids.push(r.assigned_driver_id);
    }
  }
  if (!ids.length) return { data: [], error: null };
  const { data: drivers } = await supabase
    .from("lbs_user_profiles")
    .select("id,email,first_name,last_name,phone")
    .in("id", ids);
  const byId = new Map((drivers || []).map((d) => [d.id, d]));
  return { data: ids.map((id) => byId.get(id)).filter(Boolean), error: null };
}

/* Doc requirements view ------------------------------------------------- */

export async function getDocRequirements({ clientId, siteId = null }) {
  let q = supabase
    .from("lbs_v_doc_requirements")
    .select("client_id,site_id,require_primary_doc,require_dg_manifest_if_dg,require_docs_for_direction")
    .eq("client_id", clientId);
  if (siteId) q = q.eq("site_id", siteId);
  const { data, error } = await q.limit(1);
  if (error) return { data: null, error };
  return { data: (data || [])[0] || null, error: null };
}


/* Carrier ↔ driver links (migrations 163/165) ---------------------------- */
export const linkDriverToCarrier = (approvedBookerId, driverUserId) =>
  backendCall("/api/company-drivers/link", { approved_booker_id: approvedBookerId, driver_user_id: driverUserId });
export const unlinkDriverFromCarrier = (approvedBookerId, driverUserId) =>
  backendCall("/api/company-drivers/unlink", { approved_booker_id: approvedBookerId, driver_user_id: driverUserId });

/** Carriers a driver drives for: explicit links + carriers whose bookings they're on (unless removed). */
export async function listDriverCarrierLinks(driverUserId) {
  const [links, viaBookings] = await Promise.all([
    supabase.from("lbs_company_drivers")
      .select("approved_booker_id, hidden, carrier:lbs_approved_bookers(id,name)")
      .eq("driver_user_id", driverUserId),
    supabase.from("lbs_bookings")
      .select("approved_booker_id, carrier:lbs_approved_bookers!lbs_bookings_approved_booker_id_fkey(id,name)")
      .eq("assigned_driver_id", driverUserId)
      .not("approved_booker_id", "is", null)
      .limit(200),
  ]);
  if (links.error) return { data: [], error: links.error };
  const known = new Set((links.data || []).map((l) => l.approved_booker_id));
  const derived = new Map();
  (viaBookings.data || []).forEach((b) => {
    if (!known.has(b.approved_booker_id)) derived.set(b.approved_booker_id, { ...b, via: "bookings" });
  });
  return { data: [...(links.data || []).filter((l) => !l.hidden), ...derived.values()], error: null };
}
